import logger from '@eventbrite/client-logger';
import { track } from '@eventbrite/datalayer-library';
import {
    initializeSdk,
    logEvent,
    StatsigConfiguration,
    updateUser,
} from '@eventbrite/statsig';
import { Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { CHECKOUT_FORM_NAME } from '../constants';
import { HeapEventName } from '../constants/heap';
import { STATSIG_EVENT_NAMES } from '../constants/statsig';
import {
    EB_MARKETING_OPT_IN_FIELD,
    ORG_MARKETING_OPT_IN_FIELD,
} from '../containers/checkout/constants';
import { PaymentMethodType } from '../models/paymentMethods/enums';
import {
    getIsAppRebrandActive,
    getRebrandingStatus,
    getSeriesInfiniteCalendarFF,
} from '../selectors/app';
import { getCategory, getSubcategory, isSeries } from '../selectors/event';
import { getSavedPaymentAnalyticsFeatureState } from '../selectors/forms';
import { getTotalAmount } from '../selectors/order';
import { getOrderPersistentId } from '../selectors/order/typedOrderSelectors';
import { getSelectedPaymentMethod } from '../selectors/payment';
import { getIsPaymentView } from '../selectors/routing';
import { getIsTimedEntry } from '../selectors/series';
import { getStatsigConfigFromState } from '../selectors/statsig';
import { getStripePaymentType } from '../selectors/stripe/pureSelectors';
import { getNumberOfTicketsSelected } from '../selectors/tickets/selected';
import { getHasBogoTickets } from '../selectors/ticketsAndEvent';
import { getLoginFromCheckoutPage } from '../selectors/user';
import { CheckoutState } from '../state';
import { Experiment } from '../state/statsig';
import { trackOrderSuccess } from './eventAnalytics';

export const initializeStatsigSdk = async (
    state: CheckoutState,
    stableID: string | null,
) => {
    const config: StatsigConfiguration = {
        user: getStatsigConfigFromState(state),
    };

    config.options = {
        initCompletionCallback: (
            initDurationMs: number,
            success: boolean,
            message: string | null,
        ) => {
            if (success) {
                return;
            }

            // Don't log initialization timeout errors - these occur when cookie consent blocks network requests
            // and are expected, non-fixable errors that would flood Sentry (EB-314800)
            const isInitializationTimeoutError =
                message?.includes('initialization timeout') &&
                message?.includes('network request');

            if (!isInitializationTimeoutError) {
                logger.error(
                    `Statsig initialization failed after ${initDurationMs}ms: ${message}`,
                );
            }
        },
    };

    if (stableID) {
        config.options.overrideStableID = stableID;
    }

    await initializeSdk(config);
};

export const logLoginInterstitialEvent =
    (eventName: string) =>
    (_: Dispatch<CheckoutState>, getState: () => CheckoutState) =>
        logEvent(eventName, null, {
            isInterstitialStep: `${!getLoginFromCheckoutPage(getState())}`,
        });

export const logOrderSuccessEvents =
    () =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        const state = getState();
        const orderId = getOrderPersistentId(state);
        const totalCost = getTotalAmount(state);
        const numberOfTickets = getNumberOfTicketsSelected(state);
        const paymentMethodToTrack = getPaymentMethodToTrack(state);

        logEvent(STATSIG_EVENT_NAMES.CHECKOUT_STEP_ORDER_SUCCESS, undefined, {
            ticket_quantity: `${numberOfTickets}`,
            total_cart_value: `${totalCost}`,
            payment_method: `${paymentMethodToTrack}`,
        });

        if (totalCost > 0) {
            logEvent(STATSIG_EVENT_NAMES.CHECKOUT_STEP_PAID_SUCCESS);
        }

        const checkoutFormValues = state?.form?.[CHECKOUT_FORM_NAME]?.values;
        const ebEmailCheckboxSelected =
            checkoutFormValues?.[EB_MARKETING_OPT_IN_FIELD] || false;
        const orgEmailCheckboxSelected =
            checkoutFormValues?.[ORG_MARKETING_OPT_IN_FIELD] || false;

        track({
            eventName: HeapEventName.placeOrderSuccess,
            eventData: {
                category: getCategory(state)?.title,
                ebEmailCheckboxSelected,
                has2x1: getHasBogoTickets(state),
                isSeriesInfiniteCalendar:
                    getSeriesInfiniteCalendarFF(state) && isSeries(state),
                orderId,
                orgEmailCheckboxSelected,
                rebranding_enabled: getIsAppRebrandActive(state),
                rebranding_status: getRebrandingStatus(state),
                savedPayment: getSavedPaymentAnalyticsFeatureState(state),
                selectedPaymentMethod: paymentMethodToTrack,
                subcategory: getSubcategory(state)?.title,
                // This prop will be true only for events displayed with the calendar on the listings page. For instance
                // selections made during checkout, a different prop is used, and in that case, this prop will be false.
                timedEntry: getIsTimedEntry(state),
                totalOrderValue: totalCost,
            },
        });

        // TODO: to be removed as part of GA deprecation
        dispatch(trackOrderSuccess());
    };

/**
 * Returns the payment method to track.
 * If the payment method is STRIPE_CONFIRMATION_TOKEN, it returns the Stripe payment type.
 * Otherwise, it returns the payment method type.
 */
export const getPaymentMethodToTrack = (state: CheckoutState) => {
    const selectedPaymentMethod = getSelectedPaymentMethod(state);

    if (selectedPaymentMethod !== PaymentMethodType.STRIPE_CONFIRMATION_TOKEN) {
        return selectedPaymentMethod;
    }

    const stripePaymentType = getStripePaymentType(state);
    const formattedStripeType =
        stripePaymentType && `STRIPE_${stripePaymentType.toUpperCase()}`;

    return formattedStripeType || PaymentMethodType.STRIPE_CONFIRMATION_TOKEN;
};

export const updateStatsigUserMetadata: ThunkAction<
    Promise<void>,
    CheckoutState,
    {}
> = async (_dispatch, getState) => {
    const state = getState();
    await updateUser({ user: getStatsigConfigFromState(state) });
};

export enum StatsigActionType {
    saveExperimentData = 'saveExperimentData',
}

export interface SaveExperimentDataAction {
    type: StatsigActionType.saveExperimentData;
    payload: Experiment;
}

export const saveExperimentDataAction = (
    experiment: Experiment,
): SaveExperimentDataAction => ({
    type: StatsigActionType.saveExperimentData,
    payload: experiment,
});

export type StatsigAction = SaveExperimentDataAction;

export const logPaypalPlaceOrderClickInOrderForm = (
    _dispatch: Dispatch<CheckoutState>,
    getState: () => CheckoutState,
) => {
    if (getIsPaymentView(getState())) {
        logEvent(
            STATSIG_EVENT_NAMES.CHECKOUT_STEP_PLACE_ORDER_CLICK,
            undefined,
            { payment_method: PaymentMethodType.NONCE_BRAINTREE },
        );
    }
};
